import { useQuery } from '@apollo/client'
import {
  ascend,
  assoc,
  dissoc,
  filter,
  find,
  identity,
  map,
  memoizeWith,
  pluck,
  sort,
  uniq,
} from 'ramda'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { GetOrderProductsDocument } from '../../../../../graphql/magento'
import useLogAndCaptureError from '../../../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../../../layouts/context'
import {
  isProductNil,
  isProductNotNil,
  skuProp,
  skuPropEquals,
} from '../../../../../lib/orders/utils'
import { isMagentoProductsQueryConfiguredSimpleProduct } from '../../../../../lib/products'
import { isNotNull } from '../../../../../utils/collectionTools'
import {
  OrderItemEntry,
  OrderItemEntryNoProduct,
  OrderItemEntryNoProductProps,
} from './OrderItemEntry'
import { OrderItemsPrintHeader } from './OrderItemEntry/OrderItemsPrintHeader'
import {
  OrderItemEntryData,
  OrderItemWithMaybeProduct,
  OrderItemWithProduct,
  OrderPageData,
} from './types'

const StyledItemsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: row;
  gap: 16px;
  @media print {
    background-color: ${({ theme }) => theme.colors.grayscale.white};
    display: table;
    border-collapse: collapse;
    width: 100%;
  }
`

const pluckSku = pluck('sku')

export interface OrderContentsProps {
  className?: string
  order: OrderPageData
  resolvedItems: OrderItemWithProduct[]
  reorderDisabled: boolean
  setResolvedItems: React.Dispatch<React.SetStateAction<OrderItemWithProduct[]>>
}

const OrderContents: React.FC<OrderContentsProps> = ({
  className,
  order,
  reorderDisabled,
  resolvedItems,
  setResolvedItems,
}) => {
  const [itemsNoProduct, setItemsNoProduct] = useState<OrderItemEntryNoProductProps['item'][]>([])

  const orderItems = useMemo(() => {
    return order.items?.filter(isNotNull) ?? []
  }, [order.items])

  const skusToQuery = useMemo(() => {
    const uniqueSkus = uniq(pluckSku(orderItems))
    // sort skus for deterministic arguments
    return sort(ascend(identity), uniqueSkus)
  }, [orderItems])

  const printDiscountColumn = useMemo(() => {
    return orderItems.some((orderItem) => orderItem.discountAmount > 0)
  }, [orderItems])

  const token = useContext(TokenContext)
  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  } = useQuery(GetOrderProductsDocument, {
    context: { token },
    variables: {
      sku: skusToQuery,
    },
    skip: !skusToQuery,
    // return partial data if available
    errorPolicy: 'all',
  })

  useLogAndCaptureError(productsError)

  useEffect(() => {
    if (productsLoading) {
      return
    }
    const products = productsData?.products?.items
    if (!products) {
      setResolvedItems([])
      setItemsNoProduct(orderItems)
      return
    }

    const filteredProducts = filter(isMagentoProductsQueryConfiguredSimpleProduct, products)

    const findProductForItem = memoizeWith(skuProp, (item: OrderItemEntryData) =>
      find(skuPropEquals(item), filteredProducts),
    )

    const addProductToItem = (item: OrderItemEntryData): OrderItemWithMaybeProduct =>
      assoc('product', findProductForItem(item), item)

    const addProductsToItems = (items: OrderItemEntryData[]): OrderItemWithMaybeProduct[] =>
      map(addProductToItem)(items)

    const filterHasProduct = (
      itemsWithProducts: OrderItemWithMaybeProduct[],
    ): OrderItemWithProduct[] => filter(isProductNotNil, itemsWithProducts)

    const removeProductFromItem = (item: OrderItemWithMaybeProduct): OrderItemEntryData =>
      dissoc('product', item)

    const removeProductsFromItems = (
      itemsWithProducts: OrderItemWithMaybeProduct[],
    ): OrderItemEntryData[] => map(removeProductFromItem)(itemsWithProducts)

    const filterNoProduct = (
      itemsWithProducts: OrderItemWithMaybeProduct[],
    ): OrderItemEntryData[] => removeProductsFromItems(filter(isProductNil, itemsWithProducts))

    const orderItemsWithProducts = addProductsToItems(orderItems)
    const filteredOrderItemsWithProducts = filterHasProduct(orderItemsWithProducts)

    setResolvedItems(filteredOrderItemsWithProducts)
    setItemsNoProduct(filterNoProduct(orderItemsWithProducts))
  }, [orderItems, productsData?.products?.items, productsLoading, setResolvedItems])

  return (
    <div className={className}>
      <h3 className="small-h3">Order Contents</h3>
      <StyledItemsContainer>
        <OrderItemsPrintHeader printDiscountColumn={printDiscountColumn} />
        {resolvedItems.map((item) => (
          <OrderItemEntry
            key={item.sku}
            item={item}
            reorderDisabled={reorderDisabled}
            printDiscountColumn={printDiscountColumn}
          />
        ))}
        {itemsNoProduct.map((item) => (
          <OrderItemEntryNoProduct
            key={item.sku}
            item={item}
            printDiscountColumn={printDiscountColumn}
          />
        ))}
      </StyledItemsContainer>
    </div>
  )
}

const StyledOrderContents = styled(OrderContents)({
  marginBottom: '30px',
  paddingTop: '10px',
})

export default StyledOrderContents
